import React, { useState } from 'react';
import { BrowserRouter } from 'react-router-dom';
import axios from 'axios';
import '@/App.css';

import CommandBarProvider from './components/CommandBar';
import DeleteGuardProvider from './components/DeleteGuardProvider';
import { ActionGateProvider } from './components/ActionGate';
import IdleGuard from './components/IdleGuard';
import PilotSnapshotBanner from './components/PilotSnapshotBanner';
import MustChangePasswordBanner from './components/MustChangePasswordBanner';
import UpdateBanner from './components/UpdateBanner';
import ErrorBoundary from './components/ErrorBoundary';
import { Toaster } from './components/ui/sonner';
import AppRoutes from './AppRoutes';

// ── Global toast safety net (Feb 2026) ─────────────────────────────
// Some backend endpoints reject with ``HTTPException(detail={...})``
// returning an OBJECT body (e.g. ``{error, hint}``). Pages historically
// pass ``err.response?.data?.detail`` straight to ``toast.error`` —
// Sonner then tries to render the object as a React child and crashes
// with minified React error #31, white-screening the entire app.
// We monkey-patch ``toast`` once at app bootstrap so every existing
// caller is auto-protected — non-string args are coerced via the same
// logic as ``utils/errorMessage.js`` (kept here inline because Sonner
// is imported in 70+ files and we cannot swap them all at once).
import { toast as _sonnerToast } from 'sonner';

const _coerceToast = (val) => {
  if (val == null) return '';
  if (typeof val === 'string' || typeof val === 'number' || typeof val === 'boolean') {
    return String(val);
  }
  // React elements (have $$typeof) are safe — let Sonner render them.
  if (typeof val === 'object' && val.$$typeof) return val;
  if (Array.isArray(val)) {
    if (val.length && val[0]?.msg) return val[0].msg;
    try { return val.map(_coerceToast).filter(Boolean).join(' · '); }
    catch { return ''; }
  }
  if (typeof val === 'object') {
    if (val.error && val.hint) return `${val.error}: ${val.hint}`;
    if (val.message) return String(val.message);
    if (val.detail)  return _coerceToast(val.detail);
    if (val.hint)    return String(val.hint);
    if (val.error)   return String(val.error);
    try { return JSON.stringify(val); } catch { return ''; }
  }
  return String(val);
};

(function _installToastGuard() {
  for (const m of ['error', 'success', 'warning', 'info', 'message']) {
    const orig = _sonnerToast[m];
    if (typeof orig !== 'function' || orig.__guarded) continue;
    const wrapped = (msg, opts) => orig(_coerceToast(msg), opts);
    wrapped.__guarded = true;
    _sonnerToast[m] = wrapped;
  }
})();

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const axiosInstance = axios.create({
  baseURL: API,
});

// ── Auto-refresh state (must be declared BEFORE the request interceptor
// because the proactive-refresh path references it via the TDZ-safe call
// site below). ──────────────────────────────────────────────────────────
let _refreshing = null;
async function _doRefresh() {
  const refresh_token = localStorage.getItem('refresh_token');
  if (!refresh_token) return null;
  try {
    const res = await axios.post(`${API}/auth/refresh`, { refresh_token });
    localStorage.setItem('token', res.data.token);
    localStorage.setItem('refresh_token', res.data.refresh_token);
    if (res.data.session_id) localStorage.setItem('session_id', res.data.session_id);
    return res.data.token;
  } catch (e) {
    return null;
  }
}

// ── JWT expiry helpers ─────────────────────────────────────────────────────
// Decode the `exp` claim from the access token without any crypto library.
// We don't verify the signature (server still does on each request) — we
// only need the timing so we can refresh BEFORE the access token expires.
// This eliminates the noisy "401 → refresh → 200 retry" pattern that was
// appearing on every page load in the browser console.
const _PROACTIVE_REFRESH_WINDOW_S = 60;  // refresh if expiring within 60s

function _decodeJwtExp(token) {
  try {
    const payload = token.split('.')[1];
    if (!payload) return 0;
    // base64url → base64 + padding
    const b64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    const json = JSON.parse(atob(padded));
    return typeof json.exp === 'number' ? json.exp : 0;
  } catch {
    return 0;
  }
}

function _accessTokenIsExpiringSoon() {
  const t = localStorage.getItem('token');
  if (!t) return false;  // no token → let the request flow normally (401 then login redirect)
  const exp = _decodeJwtExp(t);
  if (!exp) return false;  // can't decode → don't second-guess the server
  const now = Math.floor(Date.now() / 1000);
  return exp - now <= _PROACTIVE_REFRESH_WINDOW_S;
}

axiosInstance.interceptors.request.use(
  async (config) => {
    // Proactively rotate the access token if it's within 60s of
    // expiring. This collapses the legacy "401 → /auth/refresh → retry"
    // round-trip into a single successful request, and stops the
    // browser console from filling with red 401 lines on every page
    // load. /auth/* endpoints are exempted to avoid recursion.
    const isAuthPath = (config.url || '').includes('/auth/');
    if (!isAuthPath && _accessTokenIsExpiringSoon()) {
      _refreshing = _refreshing || _doRefresh().finally(() => { _refreshing = null; });
      const newToken = await _refreshing;
      if (newToken) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${newToken}`;
        return config;
      }
    }
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ── Auto-refresh on 401 (defensive fallback) ──────────────────────────────
// The proactive interceptor above eliminates 99% of 401s before they
// happen, but a stale token can still slip through (e.g. server clock
// drift, refresh API race). Keep the legacy "401 → refresh → retry"
// safety net so users never get bounced to /login on a transient blip.

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config || {};
    const status = error.response?.status;
    if (status === 401 && !original._retried && !original.url?.includes('/auth/')) {
      original._retried = true;
      _refreshing = _refreshing || _doRefresh().finally(() => { _refreshing = null; });
      const newToken = await _refreshing;
      if (newToken) {
        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${newToken}`;
        return axiosInstance(original);
      }
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('session_id');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);


function App() {
  const [user, setUser] = useState(() => {
    try {
      const stored = localStorage.getItem('user');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  return (
    <div className="App">
      <BrowserRouter>
        <UpdateBanner />
        <PilotSnapshotBanner />
        <MustChangePasswordBanner />
        <DeleteGuardProvider>
        <ActionGateProvider>
        <CommandBarProvider>
          <ErrorBoundary>
            <AppRoutes user={user} setUser={setUser} />
          </ErrorBoundary>
        </CommandBarProvider>
        </ActionGateProvider>
        </DeleteGuardProvider>
      </BrowserRouter>
      <Toaster position="top-right" />
      <IdleGuard />
    </div>
  );
}

export default App;